/**
 * Type Guards for Error Handling
 *
 * Provides type-safe utilities for extracting error messages and handling
 * various error formats (Error objects, API errors, strings, unknown).
 *
 * Benefits:
 * - Eliminates `(err as any).message` and `err?.message` patterns
 * - Type-safe error message extraction
 * - Consistent error handling across the codebase
 */

/**
 * Type-safe error message extraction
 *
 * Handles multiple error formats:
 * - Error instances (Error, TypeError, etc.)
 * - String errors
 * - Objects with message property
 * - Unknown types
 *
 * @param error - Error of unknown type
 * @param fallback - Fallback message if error has no message (default: 'Unknown error')
 * @returns Extracted error message
 *
 * @example
 * ```typescript
 * try {
 *   await riskyOperation()
 * } catch (e) {
 *   const message = getErrorMessage(e)
 *   console.error(message)
 * }
 * ```
 */
export function getErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }
  return fallback
}

/**
 * API error response shape
 * Common format from Tradovate API and other backend services
 */
export interface ApiError {
  /**
   * Error text from API response
   */
  errorText?: string

  /**
   * Error message (alternative to errorText)
   */
  message?: string

  /**
   * Validation violations (field-level errors)
   */
  violations?: Array<{
    fieldName?: string
    message?: string
  }>

  /**
   * HTTP status code
   */
  statusCode?: number

  /**
   * Failure reason code
   */
  failureReason?: string

  /**
   * Failure text description
   */
  failureText?: string
}

/**
 * Type guard to check if error is an API error
 *
 * @param error - Error of unknown type
 * @returns True if error matches ApiError shape
 *
 * @example
 * ```typescript
 * catch (e) {
 *   if (isApiError(e)) {
 *     console.error(e.errorText || e.message)
 *     if (e.violations) {
 *       e.violations.forEach(v => console.error(v.fieldName, v.message))
 *     }
 *   }
 * }
 * ```
 */
export function isApiError(error: unknown): error is ApiError {
  return error !== null && typeof error === 'object'
}

/**
 * Extract error message from API error
 *
 * Handles various API error formats and violations
 *
 * @param error - API error object
 * @param fallback - Fallback message (default: 'API error')
 * @returns Extracted error message
 *
 * @example
 * ```typescript
 * catch (e) {
 *   if (isApiError(e)) {
 *     const message = getApiErrorMessage(e)
 *     showNotification({ type: 'error', message })
 *   }
 * }
 * ```
 */
export function getApiErrorMessage(error: ApiError, fallback = 'API error'): string {
  // Check various error message fields
  if (error.errorText) return error.errorText
  if (error.failureText) return error.failureText
  if (error.message) return error.message

  // Check for validation violations
  if (error.violations && error.violations.length > 0) {
    const firstViolation = error.violations[0]
    if (firstViolation?.message) {
      return firstViolation.fieldName
        ? `${firstViolation.fieldName}: ${firstViolation.message}`
        : firstViolation.message
    }
  }

  // Check for failure reason code
  if (error.failureReason) {
    return `Error: ${error.failureReason}`
  }

  return fallback
}

/**
 * Combined error message extraction (works for both standard errors and API errors)
 *
 * @param error - Error of unknown type
 * @param fallback - Fallback message (default: 'Unknown error')
 * @returns Extracted error message
 *
 * @example
 * ```typescript
 * catch (e) {
 *   const message = extractErrorMessage(e, 'Operation failed')
 *   ctx.set({ error: message })
 * }
 * ```
 */
export function extractErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  if (isApiError(error)) {
    return getApiErrorMessage(error, fallback)
  }
  return getErrorMessage(error, fallback)
}
